import React from 'react'
import { BasicSelect, BasicSelectProps } from '../BasicSelect/BasicSelect'
import { useIntersectionObserverForSelect } from './useIntersectionObserverForSelect'
import { UseSelectWithPaginationProps, useSelectWithPaginationState } from './useSelectWithPaginationState'

export type SelectWithPaginationProps<Option> = UseSelectWithPaginationProps<Option> &
  Omit<BasicSelectProps<Option, false>, 'options' | 'isMulti'> & {}

/**
 * Select with pagination. First mount calls onUpdateRequested with { page: 1 }.
 * Next, on scroll to the end calls onUpdateRequested with { page: page + 1 }.
 * New options should be passed to the prop newOptions (when the reference is changed this array is added to the end of the list).
 */
export const SelectWithPagination = <Option extends any>({
  initialOptions,
  initialPage,
  onUpdateRequested,
  pageSize,
  newOptions,
  total,
  progress,
  clearDeps,
  menuMaxHeight = '50vh',
  onChange,
  ...props
}: SelectWithPaginationProps<Option>) => {
  const { options, setSearch, fetchNextPage, search } = useSelectWithPaginationState({
    initialOptions,
    initialPage,
    onUpdateRequested,
    newOptions,
    total,
    progress,
    pageSize,
    clearDeps,
  })

  const intersectionProps = useIntersectionObserverForSelect<Option, false, any>({
    ...props,
    menuMaxHeight,
    onMenuScrollToBottom: fetchNextPage,
  })

  return (
    <BasicSelect
      isSearchable
      {...intersectionProps}
      progress={progress}
      options={options}
      onSearchChange={setSearch}
      searchValue={search}
      onChange={onChange}
      {...props}
    />
  )
}
